'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H1 from 'UIComponents/elements/headings/H1';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIIntersectionObserver from 'UIComponents/observer/UIIntersectionObserver';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import CoreStorefrontHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontHeader';
import HSPageViewTracker from 'ecosystem-marketplace-core/components/tracking/HSPageViewTracker';
import { useScrollToTop } from 'ecosystem-marketplace-core/hooks/useScrollToTop';
import { getDownloadedAssetsUrl } from 'ecosystem-marketplace-core/assets/utils/urls';
import HubsParamsTracker from '../components/HubsParamsTracker';
import StorefrontSearchBar from '../components/storefront/search/StorefrontSearchBar';
import ProviderSignupBanner from '../components/storefront/ProviderSignupBanner';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import { useEmbedContext } from '../hooks/useEmbedContext';
import { getIsCustomer } from '../selectors/auth';
import { getIsPublicApp } from '../selectors/app';
import { getRootUrl, getAssetProviderSignupUrl } from '../utils/urls';
import { getBeforeUnloadTracker, getUsageTracker } from '../utils/usageTracker';
import BrowseBar from '../components/storefront/browse/BrowseBar';
const DEFAULT_EVENT_PROPERTIES = {
  screen: 'storefront',
  type: 'theme'
};
const HEADER_CUSTOM_STYLE = {
  MAIN_SECTION_CLASSNAME: 'p-top-7',
  BACKGROUND_COLOR: '#850D63',
  GRADIENT_ANGLE: '223.23deg',
  START_GRADIENT_COLOR: '#850D63',
  END_GRADIENT_COLOR: '#5C62D6'
};

const makeTrackEvent = (usageTracker, defaultEventProperties) => (eventName, eventProperties = {}) => {
  usageTracker.track(eventName, Object.assign({}, defaultEventProperties, {}, eventProperties));
};

const HeadingComponent = props => {
  const {
    pathname
  } = useLocation();

  if (pathname === '/') {
    return /*#__PURE__*/_jsx(H1, Object.assign({}, props, {
      "aria-level": 1
    }));
  }

  return /*#__PURE__*/_jsx("span", Object.assign({}, props, {
    className: "is--heading-1"
  }));
};

const StorefrontContainer = ({
  children
}) => {
  const {
    isEmbedded
  } = useEmbedContext();
  const location = useLocation();
  const {
    pathname
  } = location;
  useScrollToTop(pathname);
  const [isSticky, setIsSticky] = useState(false);
  const onStickyChange = useCallback(evt => {
    setIsSticky(evt.target.value);
  }, []);
  const [defaultEventProperties, setDefaultEventProperties] = useState(DEFAULT_EVENT_PROPERTIES);
  const handleSetDefaultEventProperties = useCallback(newDefaultEventProperties => {
    setDefaultEventProperties(Object.assign({}, DEFAULT_EVENT_PROPERTIES, {}, newDefaultEventProperties || {}));
  }, []);
  const usageTrackerContextValue = useMemo(() => {
    const trackEvent = makeTrackEvent(getUsageTracker(), defaultEventProperties);
    const trackBeforeUnloadEvent = makeTrackEvent(getBeforeUnloadTracker(), defaultEventProperties);
    return {
      trackEvent,
      trackBeforeUnloadEvent,
      trackPageview: trackEvent.bind(null, 'pageView'),
      trackFilterView: trackEvent.bind(null, 'storefront-filter-view'),
      trackInteraction: (action, eventProperties = {}) => trackEvent('interaction', Object.assign({}, eventProperties, {
        action
      })),
      trackBeforeUnloadInteraction: (action, eventProperties = {}) => trackBeforeUnloadEvent('interaction', Object.assign({}, eventProperties, {
        action
      })),
      setDefaultEventProperties: handleSetDefaultEventProperties
    };
  }, [defaultEventProperties, handleSetDefaultEventProperties]);
  const isCustomer = useSelector(getIsCustomer);
  const isPublicApp = useSelector(getIsPublicApp);

  const renderedButtonRow = /*#__PURE__*/_jsxs(UIButtonWrapper, {
    children: [/*#__PURE__*/_jsx(UIButton, {
      "data-selenium-id": "manage-apps-button",
      href: getDownloadedAssetsUrl(!isCustomer),
      size: "extra-small",
      use: "primary-white",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.storefront.header.buttons.manage"
      })
    }), /*#__PURE__*/_jsx(UIButton, {
      external: true,
      href: getAssetProviderSignupUrl(),
      iconProps: {
        className: 'hidden'
      },
      size: "extra-small",
      use: "primary-white",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.storefront.header.buttons.build"
      })
    })]
  });

  return /*#__PURE__*/_jsx(StorefrontUsageTrackerContext.Provider, {
    value: usageTrackerContextValue,
    children: /*#__PURE__*/_jsxs("div", {
      "data-test-id": "storefront-container",
      children: [/*#__PURE__*/_jsx(CoreStorefrontHeader, {
        collapsedTitle: /*#__PURE__*/_jsx(UIRouterLink, {
          to: "/",
          use: "unstyled",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.collapsedTitle"
          })
        }),
        compact: isEmbedded,
        customStyle: HEADER_CUSTOM_STYLE,
        subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.storefront.header.subtitle"
        }),
        testId: "storefront-header",
        title: /*#__PURE__*/_jsx(UIRouterLink, {
          to: "/",
          use: "unstyled",
          children: /*#__PURE__*/_jsx(HeadingComponent, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.storefront.header.title"
            })
          })
        }),
        renderedButtonRow: isPublicApp ? null : renderedButtonRow,
        onStickyChange: onStickyChange,
        children: /*#__PURE__*/_jsx(StorefrontSearchBar, {
          isSticky: isSticky
        })
      }, `storefront-header-${pathname}`), /*#__PURE__*/_jsxs("main", {
        children: [/*#__PURE__*/_jsx(BrowseBar, {}), children, /*#__PURE__*/_jsx(MainSection, {
          color: "light",
          className: "p-all-0",
          children: /*#__PURE__*/_jsx(MainSectionInner, {
            children: /*#__PURE__*/_jsx(UIIntersectionObserver, {
              onIntersectionChange: ({
                entries,
                observer
              }) => {
                if (entries[0].isIntersecting) {
                  observer.disconnect();
                }
              },
              renderContent: isIntersecting => {
                if (isIntersecting) {
                  return /*#__PURE__*/_jsx(ProviderSignupBanner, {});
                }

                return /*#__PURE__*/_jsx("div", {
                  style: {
                    height: '360px'
                  }
                });
              },
              threshold: 0.01
            })
          })
        })]
      }), /*#__PURE__*/_jsx(HSPageViewTracker, {
        pathname: `${getRootUrl()}${pathname}`
      }), /*#__PURE__*/_jsx(HubsParamsTracker, {}, pathname)]
    })
  });
};

export default StorefrontContainer;